import { useMemo, useState } from 'react'
import { ArrowUpRight, BadgeAlert, Beaker, BookOpenCheck, CircleCheck, ExternalLink, FileCheck2, FlaskConical, GitCompareArrows, History, Radar, ShieldAlert, UsersRound } from 'lucide-react'
import { Link } from 'react-router-dom'
import { PageHero } from '../components/PageHero'
import { PageMeta } from '../components/PageMeta'
import { Reveal } from '../components/Reveal'
import { RoboticSurgeryExplorer } from '../components/RoboticSurgeryExplorer'
import { researchItems, type ResearchItem } from '../data/research'

const filters: Array<'All' | ResearchItem['status']> = ['All', 'Approved use', 'Cleared device', 'Investigational']

export default function ResearchPage() {
  const [filter, setFilter] = useState<(typeof filters)[number]>('All')
  const visible = useMemo(() => researchItems.filter((item) => filter === 'All' || item.status === filter), [filter])

  return (
    <>
      <PageMeta title="Research watch" description="Explore robotic-assisted surgery, the da Vinci platform, new medical technology, approved advances, investigational therapies, and source-linked patient questions." image="/images/robotic-surgery-concept.png" imageAlt="Conceptual robotic-assisted surgery scene with a surgeon controlling instruments from a console" />
      <PageHero
        eyebrow="Research watch"
        title={<>Hope deserves context. <span className="serif-accent">Evidence deserves time.</span></>}
        copy="A plain-language look at new medical technologies and therapies—what changed, what it could mean, and what still needs to be proven."
        tone="coral"
        aside={<div className="research-radar"><Radar size={34} /><div><strong>{researchItems.length} developments</strong><span>Checked against primary or official sources</span></div></div>}
      />

      <section className="section research-art-section" id="research-lens">
        <div className="container research-art-panel">
          <Reveal className="research-art-figure">
            <img src="/images/research-future.webp" alt="Abstract 3D DNA, cell, and microchip forms in a bright scientific landscape" />
            <span>Scientific concept art · not a clinical diagram</span>
          </Reveal>
          <Reveal className="research-art-copy" delay={0.1}>
            <span className="eyebrow"><FlaskConical size={15} /> A responsible research lens</span>
            <h2>New does not automatically mean <span className="serif-accent">better.</span></h2>
            <p>Each update is read through three questions so promising headlines stay connected to real evidence and real patients.</p>
            <div className="research-lens-grid">
              <div><i>01</i><strong>What changed?</strong><span>The finding, approval, or study result—without the hype.</span></div>
              <div><i>02</i><strong>Who was studied?</strong><span>Population, trial stage, comparison, and follow-up matter.</span></div>
              <div><i>03</i><strong>What remains unknown?</strong><span>Harms, access, durability, cost, and unanswered questions.</span></div>
            </div>
          </Reveal>
        </div>
      </section>

      <RoboticSurgeryExplorer />

      <section className="research-legend-section">
        <div className="container research-legend">
          <div><CircleCheck size={20} /><span><strong>Approved use</strong> A regulator has approved a defined use; access varies.</span></div>
          <div><Beaker size={20} /><span><strong>Cleared device</strong> A defined device use has been cleared; it still has limits.</span></div>
          <div><FlaskConical size={20} /><span><strong>Investigational</strong> Still being studied; not established or approved treatment.</span></div>
        </div>
      </section>

      <section className="section research-index-section">
        <div className="container">
          <div className="research-toolbar">
            <div className="category-filters" aria-label="Filter research by status">
              {filters.map((item) => <button key={item} onClick={() => setFilter(item)} className={filter === item ? 'is-active' : ''}>{item}</button>)}
            </div>
            <span>Sources checked 3 October 2026</span>
          </div>
          <div className="research-list">
            {visible.map((item, index) => (
              <Reveal className="research-card" key={item.title} delay={(index % 2) * 0.05}>
                <div className="research-card-top">
                  <span className="research-index">{String(researchItems.indexOf(item) + 1).padStart(2, '0')}</span>
                  <span className={`status-badge status-${item.status.toLowerCase().replace(' ', '-')}`}>{item.status}</span>
                </div>
                <span className="eyebrow">{item.field} · {item.date}</span>
                <h2>{item.title}</h2>
                <p>{item.summary}</p>
                <div className="research-caution"><ShieldAlert size={18} /><span>{item.caution}</span></div>
                <div className="source-links">
                  {item.sources.map((source) => <a href={source.url} target="_blank" rel="noreferrer" key={source.url}>{source.label} <ExternalLink size={14} /></a>)}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section evidence-section">
        <div className="container evidence-panel">
          <div><span className="eyebrow">How to read research news</span><h2>Promising is not the same as proven.</h2></div>
          <div className="evidence-steps">
            <span><i>1</i><strong>Check the stage</strong><small>Lab, early trial, larger trial, or approved use?</small></span>
            <span><i>2</i><strong>Look for comparison</strong><small>Was there a control group and enough participants?</small></span>
            <span><i>3</i><strong>Keep harms visible</strong><small>Benefits only make sense beside risks and burden.</small></span>
            <span><i>4</i><strong>Ask about relevance</strong><small>Country, eligibility, access, and time all matter.</small></span>
          </div>
        </div>
      </section>

      <section className="section publication-quality-section">
        <div className="container">
          <Reveal><div className="heading-row"><div><span className="eyebrow"><BookOpenCheck size={15} /> From headline to publishable evidence</span><h2>“Full information” means showing <span className="serif-accent">how the study was done.</span></h2></div><p>A trustworthy research summary makes methods, uncertainty, harms, governance, and conflicts as visible as the exciting result.</p></div></Reveal>
          <div className="publication-quality-grid">
            {[
              { icon: UsersRound, title: 'Population & setting', copy: 'Who took part, eligibility, sample size, countries, and who may not be represented.' },
              { icon: GitCompareArrows, title: 'Design & comparison', copy: 'Research question, study design, intervention, comparator, and prespecified outcomes.' },
              { icon: BadgeAlert, title: 'Benefits, harms & uncertainty', copy: 'Absolute results, confidence intervals, adverse events, follow-up, and missing data.' },
              { icon: FileCheck2, title: 'Protocol & registration', copy: 'Trial registration, DOI or PMID, ethics review, protocol changes, and regulatory jurisdiction.' },
              { icon: ShieldAlert, title: 'Limits & conflicts', copy: 'Generalisability, important limitations, funding, author interests, and unanswered questions.' },
              { icon: History, title: 'Version & corrections', copy: 'Publication date, source-check date, reviewer, corrections, and a transparent update history.' },
            ].map((item, index) => <Reveal className="publication-quality-card" key={item.title} delay={(index % 3) * 0.04}><item.icon size={23} /><h3>{item.title}</h3><p>{item.copy}</p></Reveal>)}
          </div>
        </div>
      </section>

      <section className="section research-participation-cta-section">
        <Reveal className="container research-participation-cta">
          <img src="/images/research-participation.webp" width="1776" height="887" alt="Protected anonymous profile and paper-like information flowing into responsible research nodes" loading="lazy" />
          <div><span className="eyebrow">Community research input</span><h2>Share the question—and only the context it needs.</h2><p>Adults can submit a focused experience, barrier, or research idea through a private intake, with one optional supporting document about themselves. Contact is optional, nothing is public, and any future study or publication requires a separate process.</p><Link className="button" to="/community#research-intake">Open the research intake <ArrowUpRight size={17} /></Link></div>
        </Reveal>
      </section>

      <section className="container research-disclaimer"><FlaskConical size={25} /><div><strong>Research, not a recommendation</strong><p>Nothing here is a treatment recommendation or a promise of availability. Regulatory status and trials can change; use the linked primary sources and discuss personal questions with a qualified clinician.</p></div><a className="text-link" href="https://clinicaltrials.gov/" target="_blank" rel="noreferrer">Explore trial records <ArrowUpRight size={16} /></a></section>
    </>
  )
}
